import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Cloud, Sun, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, Wind, Droplets, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { fmtChicagoWeekday, fmtChicagoMonthDay, fmtChicagoMonthYear } from "@/utils/timezone";

const LOCATIONS = [
  { name: "Chicago O'Hare (ORD)", lat: 41.9742, lon: -87.9073 },
  { name: "Chicago Midway (MDW)", lat: 41.7868, lon: -87.7522 },
  { name: "Madison (MSN)", lat: 43.1399, lon: -89.3375 },
  { name: "Milwaukee (MKE)", lat: 42.9472, lon: -87.8966 },
  { name: "Wisconsin Dells, WI", lat: 43.6275, lon: -89.7710 }
];

const WMO_CODES = {
  0: { label: "Clear", icon: Sun, color: "text-amber-500" },
  1: { label: "Mostly Clear", icon: Sun, color: "text-amber-400" },
  2: { label: "Partly Cloudy", icon: Cloud, color: "text-gray-400" },
  3: { label: "Overcast", icon: Cloud, color: "text-gray-500" },
  45: { label: "Foggy", icon: Eye, color: "text-gray-400" },
  48: { label: "Icy Fog", icon: Eye, color: "text-blue-300" },
  51: { label: "Light Drizzle", icon: CloudDrizzle, color: "text-blue-400" },
  53: { label: "Drizzle", icon: CloudDrizzle, color: "text-blue-500" },
  55: { label: "Heavy Drizzle", icon: CloudDrizzle, color: "text-blue-600" },
  61: { label: "Light Rain", icon: CloudRain, color: "text-blue-400" },
  63: { label: "Rain", icon: CloudRain, color: "text-blue-500" },
  65: { label: "Heavy Rain", icon: CloudRain, color: "text-blue-700" },
  71: { label: "Light Snow", icon: CloudSnow, color: "text-sky-300" },
  73: { label: "Snow", icon: CloudSnow, color: "text-sky-400" },
  75: { label: "Heavy Snow", icon: CloudSnow, color: "text-sky-500" },
  80: { label: "Showers", icon: CloudRain, color: "text-blue-500" },
  81: { label: "Heavy Showers", icon: CloudRain, color: "text-blue-600" },
  82: { label: "Violent Showers", icon: CloudRain, color: "text-blue-700" },
  95: { label: "Thunderstorm", icon: CloudLightning, color: "text-purple-500" },
  96: { label: "T-storm + Hail", icon: CloudLightning, color: "text-purple-600" },
  99: { label: "T-storm + Hail", icon: CloudLightning, color: "text-purple-700" },
};

function getWeatherInfo(code) {
  return WMO_CODES[code] || { label: "Unknown", icon: Cloud, color: "text-gray-400" };
}

function getDayName(dateStr, idx) {
  if (idx === 0) return "Today";
  if (idx === 1) return "Tomorrow";
  return fmtChicagoWeekday(dateStr + "T12:00:00Z");
}

function getDateLabel(dateStr) {
  return fmtChicagoMonthDay(dateStr + "T12:00:00Z");
}

export function WeatherSection() {
  const [weather, setWeather] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(0);
  const [forecastDays, setForecastDays] = useState([]);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const results = await Promise.all(
          LOCATIONS.map(async (loc) => {
            const res = await fetch(
              `https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America/Chicago&forecast_days=16`
            );
            const data = await res.json();
            return { ...loc, current: data.current, daily: data.daily };
          })
        );
        setWeather(results);
        if (results.length > 0 && results[0].daily?.time) {
          setForecastDays(results[0].daily.time);
        }
      } catch (e) {
        console.error("Weather fetch error:", e);
      } finally { setLoading(false); }
    };
    fetchWeather();
  }, []);

  if (loading || weather.length === 0) return null;

  const currentMonthLabel = forecastDays.length > 0
    ? fmtChicagoMonthYear(forecastDays[selectedDay] + "T12:00:00Z")
    : "";

  return (
    <section className="py-6 bg-gradient-to-r from-sky-50 via-white to-sky-50 border-b" data-testid="weather-section">
      <div className="max-w-6xl mx-auto px-4">
        {/* Month header */}
        <div className="flex items-center justify-center mb-2">
          <p className="text-sm font-bold text-sky-900 uppercase tracking-wider" data-testid="weather-month-label">
            {currentMonthLabel} · 16-day forecast
          </p>
        </div>
        {/* Day selector calendar strip */}
        <div className="flex items-center justify-center gap-1 mb-4" data-testid="weather-calendar">
          <Button
            variant="ghost" size="sm" className="h-8 w-8 p-0"
            onClick={() => setSelectedDay(Math.max(0, selectedDay - 1))}
            disabled={selectedDay === 0}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex gap-1 overflow-x-auto">
            {forecastDays.map((date, idx) => {
              const dayInfo = getWeatherInfo(weather[0]?.daily?.weather_code?.[idx]);
              const DayIcon = dayInfo.icon;
              return (
                <button
                  key={date}
                  onClick={() => setSelectedDay(idx)}
                  className={`flex flex-col items-center px-3 py-1.5 rounded-xl transition-all min-w-[60px] ${
                    selectedDay === idx
                      ? "bg-sky-600 text-white shadow-md"
                      : "hover:bg-sky-100 text-gray-600"
                  }`}
                  data-testid={`weather-day-${idx}`}
                >
                  <span className="text-[10px] font-bold uppercase">{getDayName(date, idx)}</span>
                  <DayIcon className={`w-4 h-4 my-0.5 ${selectedDay === idx ? "text-white" : dayInfo.color}`} />
                  <span className="text-[10px] font-medium">{getDateLabel(date)}</span>
                </button>
              );
            })}
          </div>
          <Button
            variant="ghost" size="sm" className="h-8 w-8 p-0"
            onClick={() => setSelectedDay(Math.min(forecastDays.length - 1, selectedDay + 1))}
            disabled={selectedDay === forecastDays.length - 1}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Weather cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {weather.map((loc) => {
            const isToday = selectedDay === 0;
            const code = isToday
              ? loc.current?.weather_code
              : loc.daily?.weather_code?.[selectedDay];
            const info = getWeatherInfo(code);
            const Icon = info.icon;

            const temp = isToday
              ? Math.round(loc.current?.temperature_2m || 0)
              : null;
            const high = loc.daily?.temperature_2m_max?.[selectedDay];
            const low = loc.daily?.temperature_2m_min?.[selectedDay];
            const humidity = isToday ? loc.current?.relative_humidity_2m : null;
            const wind = isToday ? Math.round(loc.current?.wind_speed_10m || 0) : null;
            const rain = loc.daily?.precipitation_probability_max?.[selectedDay];

            return (
              <Card key={loc.name} className="border-0 shadow-md bg-white/80 backdrop-blur-sm" data-testid={`weather-card-${loc.name}`}>
                <CardContent className="p-3 text-center">
                  <div className={`w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center mx-auto mb-1.5 ${info.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="font-bold text-xs text-gray-800 truncate">{loc.name}</p>
                  {isToday && temp !== null ? (
                    <p className="text-2xl font-bold text-gray-900 my-0.5">{temp}°F</p>
                  ) : (
                    <p className="text-lg font-bold text-gray-900 my-0.5">
                      <span className="text-red-500">{Math.round(high)}°</span>
                      <span className="text-gray-400 mx-0.5">/</span>
                      <span className="text-blue-500">{Math.round(low)}°</span>
                    </p>
                  )}
                  <p className={`text-[10px] font-medium ${info.color}`}>{info.label}</p>
                  <div className="flex items-center gap-2 text-[9px] text-gray-500 justify-center mt-1">
                    {isToday && humidity !== null && (
                      <span className="flex items-center gap-0.5"><Droplets className="w-2.5 h-2.5" />{humidity}%</span>
                    )}
                    {isToday && wind !== null && (
                      <span className="flex items-center gap-0.5"><Wind className="w-2.5 h-2.5" />{wind}mph</span>
                    )}
                    {rain !== null && rain !== undefined && (
                      <span className="flex items-center gap-0.5"><CloudRain className="w-2.5 h-2.5" />{rain}%</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
